import { useDowngradeUserPlan } from '@/hooks/useDowngradeUserPlan'
import { handleDowngrade } from '@/lib/gtag'
import {
  DOWNGRADE_PLANS,
  FREE_PLAN,
  IGNITE_DOWNGRADE_COUPON,
  PRO_DOWNGRADE_COUPON,
  PRO_PLAN,
  SPARK_PLAN,
  STANDARD_PLAN,
  SUBSCRIPTION_STATUSES,
  plans,
} from '@/utils/data/plans'
import { CheckCircle, InfoCircle, XCircle } from '@mynaui/icons-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { UpgradeSuccessModal } from './CheckoutSubscriptionPlan'
import { PermissionData } from './SearchDialog'
import { ThrottledButton } from './ThrottledButton'
import { Loader } from './custom-components/Loader'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './shadcn/alert-dialog'
import { Badge } from './shadcn/badge'
import { Button } from './shadcn/button'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './shadcn/carousel'
import { DialogContent, DialogDescription, DialogHeader, DialogTitle } from './shadcn/dialog'

type CURRENT_PLAN = 'standard' | 'spark' | 'pro' | 'free'

const FREE_PLAN_ID = FREE_PLAN.plan_id as DOWNGRADE_PLANS
const SPARK_PLAN_ID = SPARK_PLAN.plan_id as DOWNGRADE_PLANS
const SPARK_NO_TRIAL_PLAN_ID = SPARK_PLAN.no_trial_plan_id as DOWNGRADE_PLANS
const STANDARD_NO_TRIAL_PLAN_ID = STANDARD_PLAN.no_trial_plan_id as DOWNGRADE_PLANS

const DowngradePlan = ({
  permissions,
  subscriptionStatus,
  currentPlan,
  scheduledSubscriptionPlan,
  closeModal,
}: {
  permissions: PermissionData
  subscriptionStatus: SUBSCRIPTION_STATUSES
  currentPlan?: CURRENT_PLAN
  scheduledSubscriptionPlan?: string
  closeModal: () => void
}) => {
  const [period] = useState<'monthly' | 'annually'>('monthly')
  const [newPlan, setNewPlan] = useState<string | null>(null)

  const userHasNoPlan = subscriptionStatus == 'hasNoPlan'

  const {
    downgradeUserPlan,
    discountedUpgrade,
    upgradeWithDiscount,
    downgradeIsPending,
    discountedUpgradeIsPending,
    upgradeDiscountIsPending,
    activateToFreeIsPending,
    isDowngradeSuccess,
    isActivationSuccess,
    isDiscountedSuccess,
    isUpgradeWithDiscountSuccess,
  } = useDowngradeUserPlan()

  function downgradePlan(selectedPlan: DOWNGRADE_PLANS, coupons?: string[]) {
    if (selectedPlan) {
      downgradeUserPlan({
        planId: selectedPlan,
        userId: permissions?.ID,
        coupons,
      })

      // find the plan details
      const plan = plans.find((item) => item.plan_id === selectedPlan)

      if (plan) {
        handleDowngrade(`${new Date()}`, parseInt(plan.price.replace('$', ''), 10) ?? 0, [
          {
            item_id: selectedPlan,
            item_name: plan?.name,
            price: parseInt(plan.price.replace('$', ''), 10) ?? 0,
            quantity: 1,
          },
        ])
      }
    } else toast.error('No plan ID was passed')
  }

  function upgradePlanWithDiscount(selectedPlan: string, coupons?: string[]) {
    if (selectedPlan) {
      upgradeWithDiscount({
        plans: [selectedPlan],
        coupons,
      })

      // find the plan details
      const plan = plans.find((item) => item.plan_id === selectedPlan)

      if (plan) {
        handleDowngrade(`${new Date()}`, parseInt(plan.price.replace('$', ''), 10) ?? 0, [
          {
            item_id: selectedPlan,
            item_name: plan?.name,
            price: parseInt(plan.price.replace('$', ''), 10) ?? 0,
            quantity: 1,
          },
        ])
      }
    } else toast.error('No plan ID was passed')
  }

  function discountUpgrade(couponCode: string) {
    if (couponCode) {
      discountedUpgrade({
        coupons: [couponCode],
      })
    } else toast.error('No coupong code was passed')
  }

  function getUpgradeType(newPlan: string, currentPlan?: CURRENT_PLAN): 'upgrade' | 'discounted' | 'downgrade' {
    const current = currentPlan
    const next = newPlan.toLowerCase()

    if (current === 'pro' && next === 'ignite plan') return 'downgrade'

    if (current === 'pro' && next === 'pro plan') return 'discounted'

    if (current === 'standard' && next === 'ignite plan') return 'discounted'

    // this section is for upgrade
    if (current === 'spark' && next === 'ignite plan') return 'upgrade'
    if (current === 'spark' && next === 'pro plan') return 'upgrade'
    if (current === 'standard' && next === 'pro plan') return 'upgrade'

    return 'downgrade'
  }

  return (
    <DialogContent
      className={`!bg-themebackground dark:!bg-themebackgrounddark ${isDowngradeSuccess || isActivationSuccess || isUpgradeWithDiscountSuccess || isDiscountedSuccess ? 'md:max-w-xl [&>button]:hidden' : 'w-full md:max-w-[95vw]'}`}
      onInteractOutside={(e) => e.preventDefault()}
      onEscapeKeyDown={(e) => e.preventDefault()}
    >
      {(isDowngradeSuccess || isActivationSuccess || isDiscountedSuccess || isUpgradeWithDiscountSuccess) && newPlan ? (
        <UpgradeSuccessModal newPlanName={newPlan} type={getUpgradeType(newPlan, currentPlan)} />
      ) : (
        <>
          <DialogHeader>
            <DialogTitle className="text-center text-xl md:text-2xl font-semibold">
              Don’t lose your inspiration, we’ll give it to you for FREE ❤️
            </DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>
          {downgradeIsPending || discountedUpgradeIsPending || upgradeDiscountIsPending || activateToFreeIsPending ? (
            <div className="flex justify-center items-center w-full py-6 h-14">
              <Loader />
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold">Available Plans</h4>
              </div>
              <div className="hidden lg:grid lg:grid-cols-4 gap-2.5">
                {FREE_PLAN && (
                  <FreePlanCard
                    currentPlan={currentPlan}
                    setNewPlan={setNewPlan}
                    userHasNoPlan={userHasNoPlan}
                    downgradePlan={downgradePlan}
                    isScheduledForDowngrade={scheduledSubscriptionPlan === FREE_PLAN_ID}
                    closeModal={closeModal}
                  />
                )}
                {SPARK_PLAN && (
                  <SparkPlanCard
                    period={period}
                    setNewPlan={setNewPlan}
                    currentPlan={currentPlan}
                    subscriptionStatus={subscriptionStatus}
                    downgradePlan={downgradePlan}
                    upgradePlanWithDiscount={upgradePlanWithDiscount}
                    isScheduledForDowngrade={
                      scheduledSubscriptionPlan === SPARK_PLAN.no_trial_plan_id ||
                      scheduledSubscriptionPlan === SPARK_PLAN.plan_id
                    }
                    closeModal={closeModal}
                  />
                )}
                {STANDARD_PLAN && (
                  <StandardPlanCard
                    period={period}
                    setNewPlan={setNewPlan}
                    currentPlan={currentPlan}
                    discountUpgrade={discountUpgrade}
                    downgradePlan={downgradePlan}
                    upgradePlanWithDiscount={upgradePlanWithDiscount}
                    isScheduledForDowngrade={
                      scheduledSubscriptionPlan === STANDARD_PLAN.no_trial_plan_id ||
                      scheduledSubscriptionPlan === STANDARD_PLAN.plan_id
                    }
                  />
                )}
                {PRO_PLAN && (
                  <ProPlanCard
                    period={period}
                    setNewPlan={setNewPlan}
                    currentPlan={currentPlan}
                    discountUpgrade={discountUpgrade}
                    upgradePlanWithDiscount={upgradePlanWithDiscount}
                  />
                )}
              </div>
              <div className="flex justify-center">
                <Carousel className="block lg:hidden md:mx-5 md:max-w-full max-w-xs ">
                  <CarouselContent>
                    {FREE_PLAN && (
                      <CarouselItem className="">
                        <FreePlanCard
                          currentPlan={currentPlan}
                          setNewPlan={setNewPlan}
                          userHasNoPlan={userHasNoPlan}
                          downgradePlan={downgradePlan}
                          isScheduledForDowngrade={scheduledSubscriptionPlan === FREE_PLAN_ID}
                          closeModal={closeModal}
                        />
                      </CarouselItem>
                    )}
                    {SPARK_PLAN && (
                      <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                        <SparkPlanCard
                          period={period}
                          setNewPlan={setNewPlan}
                          currentPlan={currentPlan}
                          subscriptionStatus={subscriptionStatus}
                          downgradePlan={downgradePlan}
                          upgradePlanWithDiscount={upgradePlanWithDiscount}
                          isScheduledForDowngrade={
                            scheduledSubscriptionPlan === SPARK_PLAN.no_trial_plan_id ||
                            scheduledSubscriptionPlan === SPARK_PLAN.plan_id
                          }
                          closeModal={closeModal}
                        />
                      </CarouselItem>
                    )}
                    {STANDARD_PLAN && (
                      <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                        <StandardPlanCard
                          period={period}
                          setNewPlan={setNewPlan}
                          currentPlan={currentPlan}
                          discountUpgrade={discountUpgrade}
                          downgradePlan={downgradePlan}
                          upgradePlanWithDiscount={upgradePlanWithDiscount}
                          isScheduledForDowngrade={
                            scheduledSubscriptionPlan === STANDARD_PLAN.no_trial_plan_id ||
                            scheduledSubscriptionPlan === STANDARD_PLAN.plan_id
                          }
                        />
                      </CarouselItem>
                    )}
                    {PRO_PLAN && (
                      <CarouselItem className="md:basis-1/2 lg:basis-1/3">
                        <ProPlanCard
                          period={period}
                          setNewPlan={setNewPlan}
                          currentPlan={currentPlan}
                          discountUpgrade={discountUpgrade}
                          upgradePlanWithDiscount={upgradePlanWithDiscount}
                        />
                      </CarouselItem>
                    )}
                  </CarouselContent>
                  <CarouselPrevious className="ml-5" />
                  <CarouselNext className="mr-5" />
                </Carousel>
              </div>
            </div>
          )}
        </>
      )}
    </DialogContent>
  )
}

export default DowngradePlan

const FreePlanCard = ({
  currentPlan,
  setNewPlan,
  userHasNoPlan,
  downgradePlan,
  isScheduledForDowngrade,
  closeModal,
}: {
  currentPlan?: CURRENT_PLAN
  setNewPlan: (plan: string) => void
  userHasNoPlan: boolean
  downgradePlan: (plan_id: DOWNGRADE_PLANS) => void
  isScheduledForDowngrade?: boolean
  closeModal: () => void
}) => {
  const [confirmShow, setConfirmShow] = useState<boolean>(false)

  return (
    <div className={'space-y-3 flex flex-col justify-between h-full'}>
      {confirmShow && (
        <ConfirmDowngrade
          open={confirmShow}
          onOpenChange={() => setConfirmShow(false)}
          newPlan="Free Plan"
          confirmHandler={() => {
            setNewPlan('Free Plan')
            downgradePlan(FREE_PLAN_ID)
          }}
          type="downgrade"
        />
      )}
      <div className={'space-y-3 p-4 border-2 rounded-lg h-full flex flex-col justify-between'}>
        <div>
          <div>
            <div className={'flex gap-2 items-center'}>
              <h3 className={'font-medium'}>{FREE_PLAN.name}</h3>
              {currentPlan === 'free' ? <Badge variant={'secondary'}>Current Plan</Badge> : null}
            </div>
            <p className={'mt-1'}>
              <span className={'text-2xl font-semibold'}>{FREE_PLAN.price}</span>
            </p>
            <p className={'text-sm font-light text-thememutedforeground'}></p>
          </div>

          <div>
            <p className={'text-sm font-light text-thememutedforeground'}>{FREE_PLAN.description}</p>
            <ul className={'space-y-3 mt-2'}>
              {FREE_PLAN.features.map((feature, index) => (
                <li key={index} className="flex gap-1 justify-start items-center text-thememutedforeground">
                  <span>
                    <CheckCircle className="w-4 h-4" />
                  </span>
                  <span className="text-sm font-normal leading-5 ">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="flex gap-2 items-center text-themedestructive">
          <span>
            <InfoCircle className="w-4 h-4" />
          </span>
          <span className="text-xs font-light flex-wrap max-w-xs">
            You will not be billed again once you select "Downgrade to Free" after your current billing cyle or trial
            expires.
          </span>
        </div>
      </div>

      <ThrottledButton
        onClick={() => {
          if (currentPlan == 'free') {
            closeModal()
          } else setConfirmShow(true)
        }}
        variant={'secondary'}
        disabled={userHasNoPlan || isScheduledForDowngrade}
        className="w-full mt-3"
      >
        {currentPlan === 'free' ? 'Keep My Plan' : 'Downgrade to Free'}
      </ThrottledButton>
    </div>
  )
}

const SparkPlanCard = ({
  currentPlan,
  setNewPlan,
  downgradePlan,
  upgradePlanWithDiscount,
  period,
  subscriptionStatus,
  isScheduledForDowngrade,
  closeModal,
}: {
  currentPlan?: CURRENT_PLAN
  setNewPlan: (plan: string) => void
  period: 'monthly' | 'annually'
  subscriptionStatus: SUBSCRIPTION_STATUSES
  downgradePlan: (plan_id: DOWNGRADE_PLANS) => void
  upgradePlanWithDiscount?: (plan_id: string, coupons?: string[]) => void
  isScheduledForDowngrade?: boolean
  closeModal: () => void
}) => {
  const [confirmShow, setConfirmShow] = useState<boolean>(false)

  return (
    <div className={'space-y-3 flex flex-col justify-between h-full'}>
      {confirmShow && (
        <ConfirmDowngrade
          open={confirmShow}
          onOpenChange={() => setConfirmShow(false)}
          newPlan="Spark Plan"
          confirmHandler={() => {
            setNewPlan('Spark Plan')
            if (currentPlan === 'free' && upgradePlanWithDiscount) upgradePlanWithDiscount(SPARK_NO_TRIAL_PLAN_ID)
            else downgradePlan(subscriptionStatus === 'hasNoPlan' ? SPARK_NO_TRIAL_PLAN_ID : SPARK_PLAN_ID)
          }}
          type={currentPlan === 'free' ? 'upgrade' : 'downgrade'}
        />
      )}
      <div className={'space-y-3 p-4 border-2 rounded-lg h-full'}>
        <div>
          <div className={'flex gap-2 items-center'}>
            <h3 className={'font-medium'}>{SPARK_PLAN.name}</h3>
            {currentPlan === 'spark' ? <Badge variant={'secondary'}>Current Plan</Badge> : null}
          </div>
          <p className={'mt-1'}>
            <span className={'text-2xl font-semibold'}>
              {period === 'monthly' ? SPARK_PLAN.price : SPARK_PLAN.annual_monthly_price}
            </span>{' '}
            / mo
          </p>
        </div>

        <div>
          <p className={'text-sm font-light text-thememutedforeground'}>{SPARK_PLAN.description}</p>
          <ul className={'space-y-3 mt-2'}>
            {SPARK_PLAN.features.map((feature, index) => (
              <li key={index} className="flex gap-1 justify-start items-center text-thememutedforeground">
                <span>
                  <CheckCircle className="w-4 h-4" />
                </span>
                <span className="text-sm font-normal leading-5 ">{feature}</span>
              </li>
            ))}
            {SPARK_PLAN.nonFeatures.map((feature, index) => (
              <li key={index} className="flex gap-1 justify-start items-center text-thememutedforeground">
                <span>
                  <XCircle className="w-4 h-4 text-red-600" />
                </span>
                <span className="text-sm font-normal leading-5 ">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Button
        onClick={() => {
          if (currentPlan === 'spark') closeModal()
          setConfirmShow(true)
        }}
        className="w-full mt-3"
        variant={currentPlan === 'spark' ? 'outline' : 'secondary'}
        disabled={isScheduledForDowngrade}
      >
        {currentPlan === 'spark' ? 'Keep My Plan' : 'Downgrade to Spark'}
      </Button>
    </div>
  )
}

const StandardPlanCard = ({
  currentPlan,
  setNewPlan,
  discountUpgrade,
  downgradePlan,
  upgradePlanWithDiscount,
  period,
  isScheduledForDowngrade,
}: {
  currentPlan?: CURRENT_PLAN
  setNewPlan: (plan: string) => void
  period: 'monthly' | 'annually'
  discountUpgrade?: (couponCode: string) => void
  downgradePlan?: (plan_id: DOWNGRADE_PLANS, coupons: string[]) => void
  upgradePlanWithDiscount?: (plan_id: string, coupons: string[]) => void
  isScheduledForDowngrade?: boolean
}) => {
  const [confirmShow, setConfirmShow] = useState<boolean>(false)

  return (
    <div className={'space-y-3 flex flex-col justify-between h-full'}>
      {confirmShow && (
        <ConfirmDowngrade
          open={confirmShow}
          onOpenChange={() => setConfirmShow(false)}
          newPlan="Ignite Plan"
          confirmHandler={() => {
            setNewPlan('Ignite Plan')

            if (currentPlan === 'pro' && downgradePlan)
              downgradePlan(STANDARD_NO_TRIAL_PLAN_ID, [IGNITE_DOWNGRADE_COUPON])

            if (currentPlan === 'standard' && discountUpgrade) discountUpgrade(IGNITE_DOWNGRADE_COUPON)

            if (!(currentPlan === 'standard' || currentPlan === 'pro') && upgradePlanWithDiscount)
              upgradePlanWithDiscount(STANDARD_NO_TRIAL_PLAN_ID, [IGNITE_DOWNGRADE_COUPON])
          }}
          type={currentPlan === 'spark' || currentPlan === 'free' ? 'upgrade' : 'downgrade'}
        />
      )}
      <div className={'space-y-3 p-4 border-2 rounded-lg h-full'}>
        <div>
          <div className={'flex gap-2 items-center'}>
            <h3 className={'font-medium'}>{STANDARD_PLAN.name}</h3>
            {currentPlan === 'standard' ? <Badge variant={'secondary'}>Current Plan</Badge> : null}
          </div>
          <p className={'mt-1'}>
            <span className={'text-2xl font-semibold line-through text-slate-500'}>
              {period === 'monthly' ? STANDARD_PLAN.price : STANDARD_PLAN.annual_monthly_price}
            </span>{' '}
            <span className={'text-2xl font-semibold'}>
              {period === 'monthly'
                ? parseInt(STANDARD_PLAN.price.replace('$', ''), 10) - STANDARD_PLAN.amount_saved_per_month
                : STANDARD_PLAN.annual_monthly_price}
            </span>{' '}
            /mo - Save ${STANDARD_PLAN.amount_saved_per_month}/mo
          </p>
        </div>

        <div>
          <p className={'text-sm font-light text-thememutedforeground'}>{STANDARD_PLAN.description}</p>
          <ul className={'space-y-3 mt-2'}>
            {STANDARD_PLAN.features.map((feature, index) => (
              <li key={index} className="flex gap-1 justify-start items-center text-thememutedforeground">
                <span>
                  <CheckCircle className="w-4 h-4" />
                </span>
                <span className="text-sm font-normal leading-5 ">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Button
        onClick={() => {
          if (currentPlan == 'standard') {
            setNewPlan('Ignite Plan')
            if (discountUpgrade) discountUpgrade(IGNITE_DOWNGRADE_COUPON)
          } else setConfirmShow(true)
        }}
        className="w-full mt-3"
        variant={currentPlan === 'standard' ? 'outline' : 'secondary'}
        disabled={isScheduledForDowngrade}
      >
        {currentPlan === 'standard' || currentPlan === 'pro' ? (
          <span className="flex items-center gap-2">
            <span>
              {currentPlan === 'standard'
                ? 'Apply Discount'
                : currentPlan === 'pro'
                  ? 'Downgrade to Ignite Now'
                  : 'Upgrade to Ignite Now'}
            </span>
          </span>
        ) : currentPlan === 'free' || currentPlan === 'spark' ? (
          'Upgrade to Ignite'
        ) : (
          'Downgrade to Ignite'
        )}
      </Button>
    </div>
  )
}

const ProPlanCard = ({
  currentPlan,
  setNewPlan,
  discountUpgrade,
  upgradePlanWithDiscount,
  period,
}: {
  currentPlan?: CURRENT_PLAN
  setNewPlan: (plan: string) => void
  period: 'monthly' | 'annually'
  discountUpgrade?: (couponCode: string) => void
  upgradePlanWithDiscount?: (plan_id: string, coupons: string[]) => void
}) => {
  const [confirmShow, setConfirmShow] = useState<boolean>(false)

  return (
    <div className={'space-y-3 flex flex-col justify-between h-full'}>
      {confirmShow && (
        <ConfirmDowngrade
          open={confirmShow}
          onOpenChange={() => setConfirmShow(false)}
          newPlan="Pro Plan"
          confirmHandler={() => {
            setNewPlan('Pro Plan')
            if (currentPlan === 'pro' && discountUpgrade) discountUpgrade(PRO_DOWNGRADE_COUPON)

            if (currentPlan !== 'pro' && upgradePlanWithDiscount)
              upgradePlanWithDiscount(PRO_PLAN.no_trial_plan_id, [PRO_DOWNGRADE_COUPON])
          }}
          type={
            currentPlan === 'standard' || currentPlan === 'spark' || currentPlan === 'free' ? 'upgrade' : 'downgrade'
          }
        />
      )}
      <div className={'space-y-3 p-4 border-2 rounded-lg h-full'}>
        <div>
          <div className={'flex gap-2 items-center'}>
            <h3 className={'font-medium'}>{PRO_PLAN.name}</h3>
            {currentPlan === 'pro' ? (
              <Badge variant={'secondary'}>Current Plan</Badge>
            ) : (
              <Badge variant={'secondaryDestructive'}>Upgrade & Save</Badge>
            )}
          </div>
          <p>
            <span className={'text-2xl font-semibold line-through text-slate-500'}>
              {period === 'monthly' ? PRO_PLAN.price : PRO_PLAN.annual_monthly_price}
            </span>{' '}
            <span className={'text-2xl font-semibold'}>
              {period === 'monthly'
                ? parseInt(PRO_PLAN.price.replace('$', ''), 10) - PRO_PLAN.amount_saved_per_month
                : PRO_PLAN.annual_monthly_price}
            </span>{' '}
            /mo - Save ${PRO_PLAN.amount_saved_per_month}/mo
          </p>
        </div>

        <div>
          <p className={'text-sm font-light text-thememutedforeground'}>{PRO_PLAN.description}</p>
          <ul className={'space-y-3 mt-2'}>
            {PRO_PLAN.features.map((feature, index) => (
              <li key={index} className="flex gap-1 justify-start items-center text-thememutedforeground">
                <span>
                  <CheckCircle className="w-4 h-4" />
                </span>
                <span className="text-sm font-normal leading-5 ">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Button
        onClick={() => {
          if (currentPlan == 'pro') {
            setNewPlan('Pro Plan')
            if (discountUpgrade) discountUpgrade(PRO_DOWNGRADE_COUPON)
          } else setConfirmShow(true)
        }}
        className="w-full mt-3"
        variant={'destructivePurple'}
      >
        <span className="flex items-center gap-2">
          <span>{currentPlan === 'pro' ? 'Upgrade & Apply Discount' : 'Upgrade to Pro Now'}</span>
        </span>
      </Button>
    </div>
  )
}

const ConfirmDowngrade = ({
  newPlan,
  open,
  onOpenChange,
  confirmHandler,
  type,
}: {
  newPlan: string
  open: boolean
  onOpenChange: () => void
  confirmHandler: () => void
  type: 'upgrade' | 'downgrade'
}) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Confirm Your {type === 'upgrade' ? 'Upgrade' : 'Downgrade'} to {newPlan}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {type === 'upgrade'
              ? "You're about to upgrade your plan. Would you like to proceed?"
              : 'You’re about to downgrade your plan. Some features from your current plan will no longer be available. Would you like to proceed?'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirmHandler}>Continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
